import { PRIVACY_PATH } from '@nemo/core/talent/consent';
import { UsersRound } from 'lucide-react';
import { useEffect } from 'react';
import { Route, Switch, useLocation } from 'wouter';
import { useSession, useSignOut } from '../auth/useSession.js';
import { Menu } from '../ui/Menu.js';
import { ApplyPage } from './ApplyPage.js';
import { ManagePage } from './ManagePage.js';
import { PrivacyPage } from './PrivacyPage.js';
import { SetPasswordPage } from './SetPasswordPage.js';
import { TalentPage } from './TalentPage.js';
import { TalentSignIn } from './TalentSignIn.js';

const APPLY_PATH = '/apply';
const MANAGE_PATH = '/manage';
const SET_PASSWORD_PATH = '/set-password';

export function TalentApp(): React.JSX.Element {
  const session = useSession();
  const signOut = useSignOut();
  const [rawLocation] = useLocation();
  const location = rawLocation.length > 1 ? rawLocation.replace(/\/+$/, '') : rawLocation;

  const onManage = location === MANAGE_PATH;

  useEffect(() => {
    document.title =
      location === APPLY_PATH
        ? 'Apply · Talent Pool'
        : location === PRIVACY_PATH
          ? 'Privacy · Talent Pool'
          : onManage
            ? 'Your profile · Talent Pool'
            : location === SET_PASSWORD_PATH
              ? 'Set your password · Talent Pool'
              : 'Talent Pool';
  }, [location, onManage]);

  // A candidate has no account, so their pages come before the gate.
  if (location === APPLY_PATH) return <ApplyPage />;
  if (location === PRIVACY_PATH) return <PrivacyPage />;
  if (onManage) return <ManagePage />;
  // Before the gate too: whoever holds the link has no session yet.
  if (location === SET_PASSWORD_PATH) return <SetPasswordPage />;
  if (session.isPending) return <p className="subtle loading">…</p>;
  if (session.data === null || session.data === undefined) return <TalentSignIn />;

  return (
    <div className="app">
      <header className="app-head">
        <strong className="brand">
          <span className="brand-mark" aria-hidden="true">
            <UsersRound />
          </span>
          Project A Talent Pool
        </strong>
        <span className="spacer" />
        <Menu
          label={`${session.data.name}, account menu`}
          className="user-button"
          items={[{ label: 'Sign out', onSelect: () => signOut.mutate() }]}
        >
          {session.data.name}
        </Menu>
      </header>

      <div className="app-body">
        <div className="app-page">
          <Switch>
            <Route path="/" component={TalentPage} />
            <Route>
              <p className="subtle">No such page.</p>
            </Route>
          </Switch>
        </div>
      </div>
    </div>
  );
}
